import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleNotch, Trash, UploadSimple, Info, CaretDown } from '@phosphor-icons/react';

import { fetchMyAccess, type ManifestSection, type SettingsManifest } from '@/lib/settingsApi';
import {
  fetchMyProfile,
  updateMyProfile,
  uploadAvatar,
  deleteAvatar,
  uploadSignature,
  deleteSignature,
  type ProfileData,
  type UpdateProfileInput,
} from '@/lib/meApi';
import { forgetAuthenticatedImage, useAuthenticatedImage } from '@/lib/authenticatedImage';
import { useAuthStore } from '@/features/auth/store';
import {
  FieldRow, Panel, SaveIndicator, SectionIntro, TextInput, useSaveState, useSettingLookup, messageOf,
} from './primitives';

/**
 * My Account — the person's own details.
 *
 * Every field here is rendered only if the server offered it, and editable only if the server
 * said so. Which fields appear differs by actor: a barrister has a year of call and an Inn, a
 * clerk does not, and neither of them is decided in this file.
 *
 * The EMAIL ADDRESS is deliberately read-only. Changing the address someone signs in with is not
 * a profile edit — it is an identity change, and doing it properly means verifying the new
 * address, holding the old one until they confirm, and recording the change where an auditor can
 * see it. None of that exists yet, so the field states the truth rather than offering a control
 * that would either fail or, worse, quietly succeed and lock someone out of their own practice.
 */

function useProfile() {
  return useQuery({ queryKey: ['me-profile'], queryFn: fetchMyProfile, staleTime: 30_000 });
}

/** A text field that saves when the person leaves it, not on every keystroke. */
function SavedText({
  value,
  onSave,
  placeholder,
  type,
  multiline,
}: {
  value: string;
  onSave: (v: string) => Promise<unknown>;
  placeholder?: string;
  type?: string;
  /** Long-form text (the professional biography): a textarea, where Enter is a new line. */
  multiline?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const { state, error, run } = useSaveState();
  const committed = useRef(value);

  // Adopt the server's value when it changes underneath us (another tab, a failed save rolled
  // back) — but never while the person is mid-edit.
  useEffect(() => {
    if (committed.current !== value) {
      committed.current = value;
      setDraft(value);
    }
  }, [value]);

  async function commit() {
    const next = draft.trim();
    if (next === committed.current) return;
    const ok = await run(() => onSave(next));
    if (ok) committed.current = next;
    else setDraft(committed.current); // A failed save must not leave the screen showing a lie.
  }

  return (
    <div>
      {multiline ? (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          placeholder={placeholder}
          rows={5}
          className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
        />
      ) : (
        <TextInput
          value={draft}
          onChange={setDraft}
          onBlur={() => void commit()}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          placeholder={placeholder}
          type={type}
        />
      )}
      <div className="mt-1 min-h-[1rem]">
        <SaveIndicator state={state} error={error} />
      </div>
    </div>
  );
}

/** Avatar or signature: choose a file, see it, remove it. */
function ImageUpload({
  kind,
  url,
  onUpload,
  onRemove,
  hint,
}: {
  kind: 'avatar' | 'signature';
  url: string | null;
  onUpload: (f: File) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
  hint: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const { state, error, run } = useSaveState();
  // The address of a replaced photo is unchanged, so the preview has to be told to look again.
  const [version, setVersion] = useState(0);
  const preview = useAuthenticatedImage(url, version);

  async function choose(file: File | undefined) {
    if (!file) return;
    await run(async () => {
      await onUpload(file);
      // The serving URL does not change when a photo is replaced, so the cached copy must go or
      // the page keeps showing the old image.
      forgetAuthenticatedImage(url);
      setVersion((v) => v + 1);
    });
    if (input.current) input.current.value = '';
  }

  return (
    <div>
      <div className="flex items-center gap-4">
        <div
          className={
            kind === 'avatar'
              ? 'flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted'
              : 'flex h-16 w-40 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-background'
          }
        >
          {preview ? (
            <img src={preview} alt={kind} className={kind === 'avatar' ? 'h-full w-full object-cover' : 'max-h-full max-w-full object-contain'} />
          ) : (
            <span className="px-2 text-center text-[10px] text-muted-foreground">Nothing uploaded</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(e) => void choose(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={state === 'saving'}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
          >
            {state === 'saving' ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <UploadSimple className="h-3.5 w-3.5" />}
            {url ? 'Replace' : 'Upload'}
          </button>
          {url && (
            <button
              type="button"
              onClick={() => void run(async () => { await onRemove(); forgetAuthenticatedImage(url); setVersion((v) => v + 1); })}
              disabled={state === 'saving'}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:text-red-600 disabled:opacity-50 dark:hover:text-red-400"
            >
              <Trash className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
      <div className="mt-1 min-h-[1rem]">
        <SaveIndicator state={state} error={error} />
      </div>
    </div>
  );
}

/** The collapse control used across Settings — same arrow, same behaviour, every section. */
function Caret({ open, onToggle, what }: { open: boolean; onToggle: () => void; what: string }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={`${open ? 'Hide' : 'Show'} ${what}`}
      className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground"
    >
      <CaretDown size={12} weight="bold" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
  );
}

/**
 * What this person is allowed to do — their roles and any capability a barrister has delegated
 * to them. Read-only everywhere: access is granted by someone else, and a screen that let you
 * edit your own permissions would not be an access control at all.
 */
function MyAccessPanel({ show }: { show: boolean }) {
  const { data } = useQuery({ queryKey: ['me-access'], queryFn: fetchMyAccess, staleTime: 60_000, enabled: show });
  const [open, setOpen] = useState(false);
  if (!show) return null;

  const capabilities = Object.entries(data?.capabilities ?? {});
  return (
    <Panel
      title="My access"
      description="Granted by your chambers or by the barristers you support"
      action={<Caret open={open} onToggle={() => setOpen((v) => !v)} what="my access" />}
    >
      {open && <div className="space-y-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Roles</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {(data?.roles ?? []).length === 0 && <span className="text-sm text-muted-foreground">None recorded</span>}
            {(data?.roles ?? []).map((r) => (
              <span key={r.key} className="rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs text-foreground">
                {r.name}
              </span>
            ))}
          </div>
        </div>
        {capabilities.length > 0 && (
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Delegated to you</div>
            <ul className="mt-1.5 space-y-1">
              {capabilities.map(([key, level]) => (
                <li key={key} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-foreground">{key.replace(/[._]/g, ' ')}</span>
                  <span className="text-xs capitalize text-muted-foreground">{String(level)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>}
    </Panel>
  );
}

export function AccountSection({ section }: { section: ManifestSection; manifest: SettingsManifest }) {
  const qc = useQueryClient();
  const setting = useSettingLookup(section);
  const { data: profile, isLoading, isError, refetch } = useProfile();
  const patchAuthUser = useAuthStore((s) => s.patchUser);
  /** "Who you are" stays open — the name and contact details are why people open this page. */
  const [openMedia, setOpenMedia] = useState(false);
  const [openPractising, setOpenPractising] = useState(false);

  const save = useMutation({
    mutationFn: (input: UpdateProfileInput) => updateMyProfile(input),
    onSuccess: (updated: ProfileData) => {
      qc.setQueryData(['me-profile'], updated);
      // The name and photo are shown in the sidebar and on every page header. Without this the
      // person changes their name in Settings and the rest of the product still calls them the
      // old one until they sign in again.
      patchAuthUser({
        firstName: updated.firstName,
        lastName: updated.lastName,
        avatarUrl: updated.avatarUrl,
      });
    },
  });

  const afterUpload = async () => {
    const fresh = await qc.fetchQuery({ queryKey: ['me-profile'], queryFn: fetchMyProfile });
    patchAuthUser({ avatarUrl: (fresh as ProfileData).avatarUrl });
  };

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center" role="status" aria-label="Loading your account">
        <CircleNotch size={20} className="animate-spin text-muted-foreground/50" />
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-center">
        <p className="text-sm font-medium text-foreground">Your account details could not be loaded</p>
        <button onClick={() => void refetch()} className="mt-3 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted">
          Try again
        </button>
      </div>
    );
  }

  const patch = (input: UpdateProfileInput) => save.mutateAsync(input);
  const barrister = profile.barristerProfile;

  return (
    <div className="space-y-5">
      <SectionIntro title="My Account" description="Your name, contact details and how you appear across the platform" />

      <Panel title="Who you are" description="Shown on matters, in the diary and to the people you work with">
        <FieldRow setting={setting('account.name')} readOnlyValue={`${profile.firstName} ${profile.lastName}`}>
          <div className="flex flex-col gap-2 sm:flex-row">
            <SavedText value={profile.firstName} placeholder="First name" onSave={(v) => patch({ firstName: v })} />
            <SavedText value={profile.lastName} placeholder="Last name" onSave={(v) => patch({ lastName: v })} />
          </div>
        </FieldRow>

        <FieldRow
          setting={setting('account.displayName')}
          hint="How your name is written where space is short"
          readOnlyValue={profile.displayName}
        >
          <SavedText value={profile.displayName ?? ''} placeholder="e.g. J. Thornton KC" onSave={(v) => patch({ displayName: v })} />
        </FieldRow>

        <FieldRow setting={setting('account.jobTitle')} readOnlyValue={profile.jobTitle}>
          <SavedText value={profile.jobTitle ?? ''} placeholder="e.g. Senior Clerk" onSave={(v) => patch({ jobTitle: v })} />
        </FieldRow>

        <FieldRow setting={setting('account.phone')} readOnlyValue={[profile.phone, profile.phone2].filter(Boolean).join(' · ')}>
          <div className="flex flex-col gap-2 sm:flex-row">
            <SavedText value={profile.phone ?? ''} placeholder="Main number" type="tel" onSave={(v) => patch({ phone: v })} />
            <SavedText value={profile.phone2 ?? ''} placeholder="Alternative" type="tel" onSave={(v) => patch({ phone2: v })} />
          </div>
        </FieldRow>

        {/* Read-only by design — see the file header. */}
        <FieldRow setting={setting('account.email')} readOnlyValue={profile.email}>
          <div className="pt-1.5">
            <div className="text-sm text-foreground">{profile.email}</div>
            <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              <span>
                This is the address you sign in with. Changing it has to be verified before it takes
                effect, so it cannot be done here — contact support to change your email.
              </span>
            </p>
          </div>
        </FieldRow>

        <FieldRow
          setting={setting('account.memberStatus')}
          readOnlyValue={<span className="capitalize">{profile.memberType.replace(/_/g, ' ')}</span>}
        />
      </Panel>

      <Panel
        title="Photo and signature"
        description="Your photo is visible to your chambers; your signature is visible only to you"
        action={<Caret open={openMedia} onToggle={() => setOpenMedia((v) => !v)} what="photo and signature" />}
      >
        {openMedia && <>
        <FieldRow setting={setting('account.avatar')} readOnlyValue={profile.avatarUrl ? 'Uploaded' : 'None'}>
          <ImageUpload
            kind="avatar"
            url={profile.avatarUrl}
            hint="JPEG, PNG, WebP or GIF, up to 5MB."
            onUpload={async (f) => { await uploadAvatar(f); await afterUpload(); }}
            onRemove={async () => { await deleteAvatar(); await afterUpload(); }}
          />
        </FieldRow>

        <FieldRow setting={setting('account.signatureImage')} readOnlyValue={profile.signatureUrl ? 'Uploaded' : 'None'}>
          <ImageUpload
            kind="signature"
            url={profile.signatureUrl}
            hint="Used where your signature appears on documents. Up to 2MB. Nobody else can view this image, including your clerks."
            onUpload={async (f) => { await uploadSignature(f); await qc.invalidateQueries({ queryKey: ['me-profile'] }); }}
            onRemove={async () => { await deleteSignature(); await qc.invalidateQueries({ queryKey: ['me-profile'] }); }}
          />
        </FieldRow>
        </>}
      </Panel>

      {(setting('account.callYear') || setting('account.inn') || setting('account.bsbNumber') || setting('account.biography')) && (
        <Panel
          title="Practising details"
          description="Your standing at the Bar"
          action={<Caret open={openPractising} onToggle={() => setOpenPractising((v) => !v)} what="practising details" />}
        >
          {openPractising && <>
          <FieldRow setting={setting('account.callYear')} readOnlyValue={barrister?.callYear ?? null}>
            <SavedText
              value={barrister?.callYear ? String(barrister.callYear) : ''}
              placeholder="e.g. 1998"
              onSave={(v) => patch({ callYear: v ? Number(v) : undefined })}
            />
          </FieldRow>
          <FieldRow setting={setting('account.inn')} readOnlyValue={barrister?.inn}>
            <SavedText value={barrister?.inn ?? ''} placeholder="e.g. Middle Temple" onSave={(v) => patch({ inn: v })} />
          </FieldRow>
          <FieldRow setting={setting('account.bsbNumber')} readOnlyValue={barrister?.bsbAuthNumber}>
            <SavedText value={barrister?.bsbAuthNumber ?? ''} placeholder="BSB authorisation number" onSave={(v) => patch({ bsbAuthNumber: v })} />
          </FieldRow>
          {/* account.biography is Barrister.biography (settings registry) — the professional
              biography shown on the public profile. It was bound to shortBio when the old profile
              page was retired, which left the biography the public profile displays uneditable. */}
          <FieldRow setting={setting('account.biography')} readOnlyValue={barrister?.biography}>
            <SavedText multiline value={barrister?.biography ?? ''} placeholder="Write a brief professional biography…" onSave={(v) => patch({ biography: v })} />
          </FieldRow>
          </>}
        </Panel>
      )}

      <MyAccessPanel show={!!setting('account.myAccess')} />

      {save.isError && (
        <p className="text-xs text-red-600 dark:text-red-400">{messageOf(save.error)}</p>
      )}
    </div>
  );
}
